import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy that handles chunk loading failures gracefully.
 * On failure, it will attempt one reload. If that fails, it redirects to /reset.html.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  const RETRY_KEY = 'chunk-retry-attempted';
  
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      // Check if we've already tried to reload for this session
      const hasRetried = sessionStorage.getItem(RETRY_KEY);
      
      if (!hasRetried) {
        // First failure: mark as retried and reload the page
        sessionStorage.setItem(RETRY_KEY, 'true');
        console.warn('[lazyWithRetry] Chunk load failed, reloading page...', error);
        window.location.reload();
        // Return a never-resolving promise to prevent React from rendering
        return new Promise(() => {});
      } else {
        // Second failure: redirect to reset page
        console.error('[lazyWithRetry] Chunk load failed after retry, redirecting to reset...', error);
        sessionStorage.removeItem(RETRY_KEY);
        window.location.href = '/reset.html?auto=1';
        // Return a never-resolving promise to prevent React from rendering
        return new Promise(() => {});
      }
    }
  });
}

/**
 * Clear the retry flag. Call this on successful app mount
 * to reset the retry mechanism for future navigations.
 */
export function clearRetryFlag(): void {
  sessionStorage.removeItem('chunk-retry-attempted');
}
